import { ApiError } from '@/api/types'
import { addStockQuantities } from '@/shared/lib/stock-quantity'

/**
 * The rules a capture attempt follows, kept out of the components so they can be tested directly.
 *
 * The design decision behind all of this is "capture resilience is a safe resend, not an offline
 * queue": at most one capture is ever in flight, a failure keeps the typed quantity on screen, and
 * a resend repeats the *same* `captureId` so the server treats it as the no-op it is. Nothing is
 * persisted — the session dies with the tab either way, and a queue would drag reconciliation,
 * review blocking and compensating undo entries behind it.
 */

/** What went wrong, in the three flavours the counter needs to be told apart. */
export type CaptureFailureKind =
  /** 409 `session_not_open` — somebody closed or aborted the count; nothing can be captured. */
  | 'session_closed'
  /** 400 `already_resolved` — the correction was applied at review; the row is now the audit trail. */
  | 'already_resolved'
  /** A timeout, a dead spot, a 5xx: unknown outcome, and exactly what the resend exists for. */
  | 'connection'
  /**
   * 409 `product_has_no_stock` — a dish, a variant or a not-tracked article
   * (CHG-2026-09-stock-usage-and-price-twins, D11): it is never counted, so a resend cannot help.
   */
  | 'no_stock'
  /**
   * 400 `whole_quantity_required` — a decimal for an article counted in whole numbers: "Teilmengen
   * zählen" was switched off since the assortment was read. Resending the same decimal cannot help.
   */
  | 'whole_required'
  /** 400 `invalid_quantity` — more than 3 decimals or beyond NUMERIC(12,3); never sent as typed. */
  | 'invalid_quantity'

export function classifyCaptureFailure(error: unknown): CaptureFailureKind {
  if (error instanceof ApiError) {
    if (error.status === 409 && error.code === 'product_has_no_stock') {
      return 'no_stock'
    }
    if (error.status === 409) return 'session_closed'
    if (error.status === 400 && error.code === 'already_resolved') {
      return 'already_resolved'
    }
    if (error.status === 400 && error.code === 'whole_quantity_required') {
      return 'whole_required'
    }
    if (error.status === 400 && error.code === 'invalid_quantity') {
      return 'invalid_quantity'
    }
  }
  return 'connection'
}

/**
 * A fresh idempotency key. `crypto.randomUUID` needs a secure context, which the backoffice
 * always has in production but not in every test or dev host, hence the fallback — the value only
 * has to be unique per device, not unguessable.
 */
export function newCaptureId(): string {
  // Typed loosely on purpose: `randomUUID` is declared as always present, but it is absent in
  // an insecure context and in some test environments.
  const host = globalThis as { crypto?: { randomUUID?: () => string } }
  if (typeof host.crypto?.randomUUID === 'function') {
    return host.crypto.randomUUID()
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

/**
 * What the SKU's running total becomes if this entry is accepted. Added in integer thousandths
 * (CHG-2026-09-stock-usage-and-price-twins): whole numbers add exactly as before, and 1,3 + 0,2
 * is 1,5 rather than 1.5000000000000002.
 */
/**
 * Failures that say the article's counting mode on this device is stale: the assortment is read
 * again, so the next time the pad opens it matches the server (whole numbers again).
 */
export function isCountingModeFailure(
  kind: CaptureFailureKind | null,
): boolean {
  return kind === 'whole_required' || kind === 'invalid_quantity'
}

export function resultingTotal(
  previousQty: number | null,
  qty: number,
  mode: 'add' | 'replace',
): number {
  return mode === 'replace' ? qty : addStockQuantities(previousQty, qty)
}

/**
 * How to take back the last capture.
 *
 * Setting the total back to 0 would record "counted 0" — a deliberate statement that the article
 * is not there, which review then treats as a shortage to confirm. Undoing a capture that had no
 * predecessor has to remove the row instead, which is what `deleteCapture` is for.
 */
export type UndoPlan = { kind: 'delete' } | { kind: 'set'; qty: number }

export function undoPlan(previousTotal: number | null): UndoPlan {
  if (previousTotal == null || previousTotal <= 0) return { kind: 'delete' }
  return { kind: 'set', qty: previousTotal }
}
